import { useCallback, useMemo } from 'react';
import { useLogger } from '@direct-frontend/modules-logger';
import { UnexpectedError } from '@adv-frontend/error';
import { BaseRowData } from '@adv-frontend/data-provider';
import { useMountEffect, useToggle } from '@direct-frontend/stdlib';
import { isValuesOfValueType } from '../../lib/helpers/isValuesOfValueType';
import { OperatorType } from '../../lib/types/Operator';
import { FilterSelectList } from './FilterSelectList';
import type { SelectWithSuggestFilterProps } from '../../lib/types/Filters';

export function SelectWithSuggestFilter<RowData extends BaseRowData = BaseRowData, ValueType extends string = string>({
    id,
    title,
    value,
    color = 'normal',
    onRemove,
    onChange,
    dataProvider,
    useTitleByValue,
    renderSelectListItem,
    renderFilterChipsItem,
    wasAutoFocused,
    onFocus,
    operator,
    operators,
    operatorTitles,
    shownValuesLimit,
    showSelectedOnlySwitcher,
    renderValuesLimitText,
    suggestPlaceholder,
    isDisabled,
    isRequired,
    errors,
    addonLeft,
    valueType,
    'data-testid': dataTestId,
}: SelectWithSuggestFilterProps<RowData, ValueType>) {
    const nodeInfo = useMemo(
        () => ({
            name: 'SelectListFilter',
            attrs: {
                id,
            },
        }),
        [id]
    );
    const { LogProvider } = useLogger(nodeInfo);

    const { value: isOpen, toggleOn: open, toggleOff: close } = useToggle(!wasAutoFocused && value.length === 0);

    const onMultiChange = useCallback(
        (newValue: string[]) => {
            if (isValuesOfValueType<ValueType>(newValue, valueType)) {
                onChange({ value: newValue, operator });
            } else {
                console.error(
                    new UnexpectedError('SelectWithSuggestFilter получил значения неожиданного типа', {
                        cause: undefined,
                        additional: { newValue },
                    })
                );
            }
        },
        [onChange, operator, valueType]
    );

    const onChangeOperator = useCallback(
        (operator: OperatorType) => {
            onChange({ value, operator });
        },
        [onChange, value]
    );

    useMountEffect(() => {
        if (!wasAutoFocused) {
            onFocus();
        }
    });

    return (
        <LogProvider>
            <FilterSelectList<RowData>
                errors={errors}
                size="xs"
                color={color}
                isOpen={isOpen}
                onOpen={open}
                onClose={close}
                placeholder={title}
                value={value}
                dataProvider={dataProvider}
                operator={operator}
                operators={operators}
                operatorTitles={operatorTitles}
                onRemove={onRemove}
                isRequired={isRequired}
                onChangeOperator={onChangeOperator}
                renderSelectListItem={renderSelectListItem}
                renderFilterChipsItem={renderFilterChipsItem}
                useTitleByValue={useTitleByValue}
                onChange={onMultiChange}
                shownValuesLimit={shownValuesLimit}
                showSelectedOnlySwitcher={showSelectedOnlySwitcher}
                renderValuesLimitText={renderValuesLimitText}
                data-testid={dataTestId}
                suggestPlaceholder={suggestPlaceholder}
                logNode="FilterSelectList"
                isDisabled={isDisabled}
                addonLeft={addonLeft}
            />
        </LogProvider>
    );
}
